import { useEffect, useMemo, useState } from "react";
import { api, detailText } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Check, X, CheckSquare, Sparkles, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useT } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import PremiumPaymentModal from "@/components/app/PremiumPaymentModal";

// Bobot prioritas untuk tie-breaker urutan (high paling atas)
const PRIORITY_WEIGHT = { high: 0, medium: 1, low: 2 };

export default function Checklist() {
  const { t } = useT();
  const { user, isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  // initialLoading hanya untuk render pertama. Refetch/mutasi tidak boleh
  // mengosongkan halaman (spinner full-page me-reset scroll <main>).
  const [initialLoading, setInitialLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [paymodalOpen, setPaymodalOpen] = useState(false);

  const handleApplyClick = () => {
    if (!isAdmin && user?.plan_type === "trial") {
      toast.error("Fitur 34 Template Task khusus pengguna Paket Premium ✨");
      setPaymodalOpen(true);
      return;
    }
    if (tasks.length > 0) {
      setShowResetConfirm(true);
    } else {
      executeApplyTemplate();
    }
  };

  const executeApplyTemplate = async () => {
    setShowResetConfirm(false);
    setApplyingTemplate(true);
    try {
      const { data } = await api.post("/checklist/apply-template");
      setTasks(data.tasks || []);
      toast.success("Berhasil menerapkan template checklist!");
    } catch (e) {
      toast.error(detailText(e) || "Gagal menerapkan template checklist");
      console.error(e);
    } finally {
      setApplyingTemplate(false);
    }
  };
  const [newTask, setNewTask] = useState({
    title: "",
    category: "Planning",
    due_date: "",
    priority: "medium",
  });
  // State untuk menyimpan task yang sedang diedit (berisi task_id dan field2 yang diubah)
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    category: "",
    due_date: "",
    priority: "",
  });

  const loadTasks = async () => {
    setInitialLoading(true);
    try {
      const { data } = await api.get("/checklist");
      setTasks(data.tasks || []);
    } catch (e) {
      toast.error("Gagal memuat checklist");
      console.error(e);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  // ===== Filter & urutan =====
  // priorityFilter: "all" | "high" | "medium" | "low"
  // dateSort: "nearest" (default, tanggal terdekat paling atas) | "none" (urutan asli)
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [dateSort, setDateSort] = useState("nearest");

  const visibleTasks = useMemo(() => {
    let list = [...tasks];
    if (priorityFilter !== "all") {
      list = list.filter((tk) => (tk.priority || "medium") === priorityFilter);
    }
    if (dateSort === "nearest") {
      // Tugas tanpa deadline ditaruh paling bawah
      const ts = (d) => (d ? new Date(d).getTime() : Infinity);
      list.sort((a, b) => {
        const da = ts(a.due_date);
        const db = ts(b.due_date);
        if (da !== db) return da - db;
        // Tanggal sama → urutkan berdasarkan prioritas
        return (
          (PRIORITY_WEIGHT[a.priority || "medium"] ?? 1) -
          (PRIORITY_WEIGHT[b.priority || "medium"] ?? 1)
        );
      });
    }
    return list;
  }, [tasks, priorityFilter, dateSort]);

  const filtersActive = priorityFilter !== "all" || dateSort !== "nearest";
  const resetFilters = () => {
    setPriorityFilter("all");
    setDateSort("nearest");
  };

  // Tambah tugas (optimistic: tampilkan duluan, rekonsiliasi dengan server)
  const addTask = async () => {
    if (!newTask.title.trim()) {
      toast.error("Judul tugas wajib diisi");
      return;
    }
    const tempId = `tmp_${Date.now()}`;
    const optimistic = {
      task_id: tempId,
      title: newTask.title.trim(),
      category: newTask.category,
      due_date: newTask.due_date || null,
      priority: newTask.priority,
      status: "todo",
    };
    setSubmitting(true);
    setTasks((prev) => [...prev, optimistic]);
    setNewTask({ title: "", category: "Planning", due_date: "", priority: "medium" });
    try {
      const { data } = await api.post("/checklist", {
        title: optimistic.title,
        category: optimistic.category,
        due_date: optimistic.due_date,
        priority: optimistic.priority,
      });
      if (data?.task) {
        setTasks((prev) =>
          prev.map((tk) => (tk.task_id === tempId ? data.task : tk))
        );
      }
      toast.success("Tugas berhasil ditambahkan ✅");
    } catch (e) {
      setTasks((prev) => prev.filter((tk) => tk.task_id !== tempId));
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menambah tugas");
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle status (optimistic: balik status duluan, rollback bila gagal)
  const toggleStatus = async (task) => {
    const newStatus = task.status === "completed" ? "todo" : "completed";
    patchLocal(task.task_id, { status: newStatus });
    try {
      const { data } = await api.patch(`/checklist/${task.task_id}`, { status: newStatus });
      if (data?.task) patchLocal(task.task_id, data.task);
    } catch (e) {
      patchLocal(task.task_id, { status: task.status });
      toast.error(detailText(e?.response?.data?.detail) || "Gagal mengupdate status");
    }
  };

  // Hapus tugas (optimistic: hapus dari list duluan, kembalikan bila gagal)
  const deleteTask = async (taskId) => {
    if (!confirm("Yakin ingin menghapus tugas ini?")) return;
    const snapshot = tasks;
    setTasks((prev) => prev.filter((tk) => tk.task_id !== taskId));
    try {
      await api.delete(`/checklist/${taskId}`);
      toast.success("Tugas dihapus");
    } catch (e) {
      setTasks(snapshot);
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menghapus");
    }
  };

  // Mulai mode edit
  const startEdit = (task) => {
    setEditingId(task.task_id);
    setEditForm({
      title: task.title,
      category: task.category || "Planning",
      due_date: task.due_date || "",
      priority: task.priority || "medium",
    });
  };

  // Batalkan edit
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: "", category: "", due_date: "", priority: "" });
  };

  // Simpan hasil edit (optimistic: terapkan lokal & tutup editor seketika)
  const saveEdit = async (taskId) => {
    if (!editForm.title.trim()) {
      toast.error("Judul tidak boleh kosong");
      return;
    }
    const prev = tasks.find((tk) => tk.task_id === taskId);
    const applied = {
      title: editForm.title.trim(),
      category: editForm.category,
      // Kirim string kosong (bukan null) agar backend menghapus deadline;
      // backend PATCH membuang nilai None/null.
      due_date: editForm.due_date || "",
      priority: editForm.priority,
    };
    patchLocal(taskId, applied);
    setEditingId(null);
    try {
      const { data } = await api.patch(`/checklist/${taskId}`, applied);
      if (data?.task) patchLocal(taskId, data.task);
      toast.success("Tugas diperbarui ✅");
    } catch (e) {
      if (prev) patchLocal(taskId, prev);
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menyimpan perubahan");
    }
  };

  // Helper render deadline dengan warna
  const renderDeadline = (dueDate, status) => {
    if (!dueDate) return <span className="text-muted-foreground text-sm">-</span>;

    const dateStr = new Date(dueDate).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    if (status === "completed") {
      return <span className="text-muted-foreground font-normal">{dateStr}</span>;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));

    let colorClass = "text-muted-foreground";
    let label = "";

    if (diffDays < 0) {
      colorClass = "text-red-500 font-medium";
      label = "⚠️ Terlewat";
    } else if (diffDays === 0) {
      colorClass = "text-orange-500 font-medium";
      label = "Hari ini!";
    } else if (diffDays <= 3) {
      colorClass = "text-yellow-600 font-medium";
      label = `${diffDays} hari lagi`;
    } else {
      label = `${diffDays} hari lagi`;
    }

    return (
      <span className={colorClass}>
        {dateStr} {label !== dateStr && `(${label})`}
      </span>
    );
  };

  // Patch satu task di state lokal (untuk optimistic update).
  const patchLocal = (taskId, patch) =>
    setTasks((prev) =>
      prev.map((tk) => (tk.task_id === taskId ? { ...tk, ...patch } : tk))
    );

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-10">
      <div className="flex flex-wrap justify-between items-end gap-3 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("checklist.section")}</div>
          <h1 className="font-serif text-4xl flex items-center gap-2">
            <CheckSquare className="h-7 w-7 text-primary" /> {t("checklist.title")}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleApplyClick}
            disabled={applyingTemplate}
            data-testid="apply-checklist-template-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-primary/30 bg-primary/10 hover:bg-primary/20 text-xs font-semibold text-primary transition-all cursor-pointer shadow-sm disabled:opacity-50"
          >
            {applyingTemplate ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Pakai Template
          </button>
          <Badge variant="secondary" className="rounded-full" data-testid="checklist-progress-badge">
            {tasks.filter((task) => task.status === "completed").length} / {tasks.length} selesai
          </Badge>
        </div>
      </div>

      {/* Form tambah tugas */}
      <div className="mb-6 p-4 border border-primary/20 rounded-lg bg-muted/30">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Judul</label>
            <input
              type="text"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              placeholder="Misal: Pesan katering"
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Kategori</label>
            <select
              value={newTask.category}
              onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            >
              <option value="Planning">Planning</option>
              <option value="Venue">Venue</option>
              <option value="Catering">Catering</option>
              <option value="Photography">Photography</option>
              <option value="Videography">Videography</option>
              <option value="Attire">Attire</option>
              <option value="Decoration">Decoration</option>
              <option value="Makeup">Makeup</option>
              <option value="Entertainment">Entertainment</option>
              <option value="Invitation">Invitation</option>
              <option value="Transportation">Transportation</option>
              <option value="Others">Others</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Deadline</label>
            <input
              type="date"
              value={newTask.due_date}
              onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            />
          </div>
          <button
            onClick={addTask}
            disabled={submitting}
            className="w-full md:w-auto px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center"
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Tambah
          </button>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Tip: Tentukan deadline agar tugas muncul di dashboard "This week".
        </div>
      </div>

      {/* Filter & urutan */}
      <div className="mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Prioritas</label>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            data-testid="checklist-priority-filter"
            className="w-full px-3 py-2 border rounded-md bg-background text-sm"
          >
            <option value="all">Semua prioritas</option>
            <option value="high">Tinggi</option>
            <option value="medium">Sedang</option>
            <option value="low">Rendah</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Urutkan</label>
          <select
            value={dateSort}
            onChange={(e) => setDateSort(e.target.value)}
            data-testid="checklist-sort-filter"
            className="w-full px-3 py-2 border rounded-md bg-background text-sm"
          >
            <option value="nearest">Tanggal terdekat</option>
            <option value="none">Tanpa urutan</option>
          </select>
        </div>
        {filtersActive && (
          <button
            type="button"
            onClick={resetFilters}
            className="px-3 py-2 text-sm border rounded-md hover:bg-accent/70 transition-colors flex items-center gap-1.5"
          >
            <X className="h-3.5 w-3.5" /> Reset
          </button>
        )}
        <span className="text-xs text-muted-foreground ml-auto pb-2.5" data-testid="checklist-filter-count">
          Menampilkan {visibleTasks.length} dari {tasks.length} tugas
        </span>
      </div>

      {/* Daftar tugas */}
      <div className="space-y-3">
        {tasks.length === 0 ? (
          <div className="text-center text-muted-foreground py-12 px-6 rounded-2xl border border-dashed border-border bg-card/40 my-4">
            <CheckSquare className="h-10 w-10 text-primary/40 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-foreground mb-1">Checklist Anda Masih Kosong</h3>
            <p className="text-xs text-muted-foreground max-w-md mx-auto mb-5 leading-relaxed">
              Tambahkan tugas secara manual di atas, atau klik tombol di bawah untuk menggunakan 34 template tugas persiapan pernikahan pilihan (6 bulan - setelah acara).
            </p>
            <button
              type="button"
              onClick={handleApplyClick}
              disabled={applyingTemplate}
              data-testid="empty-apply-template-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-xs hover:bg-primary/90 transition-all shadow-sm cursor-pointer disabled:opacity-50"
            >
              {applyingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Pakai Template Checklist (34 Tugas)
            </button>
          </div>
        ) : visibleTasks.length === 0 ? (
          <div className="text-center text-muted-foreground py-16">
            <p className="text-lg">Tidak ada tugas yang cocok dengan filter.</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-3 text-sm text-primary hover:underline"
            >
              Hapus filter
            </button>
          </div>
        ) : (
          visibleTasks.map((task) => (
            <div
              key={task.task_id}
              data-testid={`checklist-task-${task.task_id}`}
              className={`border-l-4 p-4 rounded-r-lg shadow-sm bg-card ${
                task.status === "completed"
                  ? "border-green-500 opacity-60"
                  : "border-primary"
              }`}
            >
              {editingId === task.task_id ? (
                // MODE EDIT
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Judul</label>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Kategori</label>
                      <select
                        value={editForm.category}
                        onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                      >
                        <option value="Planning">Planning</option>
                        <option value="Venue">Venue</option>
                        <option value="Catering">Catering</option>
                        <option value="Photography">Photography</option>
                        <option value="Videography">Videography</option>
                        <option value="Attire">Attire</option>
                        <option value="Decoration">Decoration</option>
                        <option value="Makeup">Makeup</option>
                        <option value="Entertainment">Entertainment</option>
                        <option value="Invitation">Invitation</option>
                        <option value="Transportation">Transportation</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Deadline</label>
                      <input
                        type="date"
                        value={editForm.due_date}
                        onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveEdit(task.task_id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1"
                      >
                        <Check className="h-4 w-4" /> Simpan
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 flex items-center gap-1"
                      >
                        <X className="h-4 w-4" /> Batal
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // MODE TAMPIL BIASA
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                    <input
                      type="checkbox"
                      checked={task.status === "completed"}
                      onChange={() => toggleStatus(task)}
                      data-testid={`checklist-toggle-${task.task_id}`}
                      className="h-4 w-4 mt-1 accent-primary"
                    />
                    <div className="flex-1">
                      <div
                        className={`font-medium ${
                          task.status === "completed" ? "line-through text-muted-foreground" : ""
                        }`}
                      >
                        {task.title}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-1">
                        <span className="border px-2 py-0.5 rounded-full text-[10px]">
                          {task.category || "Planning"}
                        </span>
                        <span className="bg-secondary px-2 py-0.5 rounded-full text-[10px] capitalize">
                          {task.priority}
                        </span>
                        <span className="flex items-center gap-1">
                          <span>📅</span> {renderDeadline(task.due_date, task.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEdit(task)}
                      className="p-1 rounded-md hover:bg-primary/10 text-primary"
                      title="Edit tugas"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteTask(task.task_id)}
                      className="p-1 rounded-md hover:bg-destructive/10 text-destructive"
                      title="Hapus tugas"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal Konfirmasi Reset Template */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="sm:max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-5 w-5 shrink-0" /> Reset &amp; Terapkan Template?
            </DialogTitle>
            <DialogDescription className="pt-2 text-xs text-muted-foreground leading-relaxed">
              Checklist Anda saat ini telah berisi <strong className="text-foreground">{tasks.length} tugas</strong>. Menerapkan ulang template akan menghapus seluruh tugas lama dan mereset progress dari awal. Apakah Anda yakin ingin melanjutkan?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setShowResetConfirm(false)} className="rounded-xl">
              Batal
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={executeApplyTemplate}
              disabled={applyingTemplate}
              className="rounded-xl"
            >
              {applyingTemplate ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Ya, Reset &amp; Terapkan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PremiumPaymentModal open={paymodalOpen} onOpenChange={setPaymodalOpen} />
    </div>
  );
}